import React, {
  useMemo,
  memo,
} from 'react';
import { useLocation } from 'react-router-dom';
import PropTypes from 'prop-types';
import {
  Modal,
  ModalBody,
  ModalHeader,
  Label,
  Col,
  Row,
  Input,
} from 'reactstrap';
import Flatpickr from 'react-flatpickr';
import * as Yup from 'yup';
import { useFormik } from 'formik';

import { ReactComponent as CloseButtonSvg } from 'assets/images/location/close-button-svg.svg';

import AppInputNumberValidation from 'components/App/InputNumberValidation';
import Button from 'components/App/Button';

import INITIAL_VALUE from 'constants/initialValue';
import { parseCurrency, unparseCurrency } from 'utils';
import { editRoomValidation } from 'utils/validate';
import AppSelectValidation from 'components/App/SelectValidation';

import styles from './ModalEditPolicyReceiveRoom.module.scss';

const depositTypeOptions = [
  { value: 'AMOUNT', label: 'Số tiền' },
  { value: 'PERCENT', label: 'Phần trăm' },
];

const optionsFlatpickr = {
  enableTime: true,
  noCalendar: true,
  dateFormat: 'H:i',
  minuteIncrement: 1,
  time_24hr: true,
};

function ModalEditPolicyReceiveRoom(props) {
  const {
    checkInTime,
    checkOutTime,
    depositPolicy,

    isOpen,

    actionGetPlaceDetail,
    actionEditReceiveRoomPolicy,
    onToggleModal,
  } = props;

  const location = useLocation();
  const placeId = new URLSearchParams(location.search).get('place');

  const onCallBackSave = React.useCallback(() => {
    onToggleModal();
    actionGetPlaceDetail({ placeId });
  }, [actionGetPlaceDetail, onToggleModal, placeId]);

  const validation = useFormik({
    enableReinitialize: true,
    initialValues: {
      checkInTime,
      checkOutTime,
      allowDeposit: depositPolicy.allowDeposit,
      depositType: depositPolicy.amount ? 'AMOUNT' : 'PERCENT',
      amount: depositPolicy.amount || 0,
      depositPercent: depositPolicy.depositPercent || 0,
    },
    validationSchema: Yup.object(editRoomValidation),
    onSubmit: (values) => {
      const result = {
        placeId,
        checkInTime: values.checkInTime,
        checkOutTime: values.checkOutTime,
        depositPolicy: {
          allowDeposit: values.allowDeposit,
          ...(values.depositType === 'AMOUNT' ? { amount: parseCurrency(values.amount), depositPercent: 0 } : { amount: 0, depositPercent: parseCurrency(values.depositPercent) }),
        },
        onCallBackSave,
      };

      actionEditReceiveRoomPolicy(result);
    },
  });

  const handleDepositChange = React.useCallback((e) => {
    const selectedValue = !!(+e.target.value);
    validation.setFieldValue('allowDeposit', selectedValue);
    if (!selectedValue) {
      validation.setFieldValue('amount', 0);
      validation.setFieldValue('depositPercent', 0);
    }

    validation.setFieldError('amount', null);
    validation.setFieldError('depositPercent', null);
    validation.setFieldTouched('amount', false);
    validation.setFieldTouched('depositPercent', false);
  }, []);

  const onChangeFilterOption = React.useCallback((e) => {
    validation.setFieldValue(e.name, e.value, true);
  }, [validation]);

  const handleTimeChange = React.useCallback((_, currentDate, self) => {
    validation.setFieldValue(self.element.name, currentDate);
  }, []);

  const CloseButton = useMemo(() => (
    <button className={styles.ButtonClose} onClick={onToggleModal} type="button">
      <CloseButtonSvg />
    </button>
  ), [onToggleModal]);

  return (
    <div className={styles.Component}>
      <Modal
        fade
        size="lg"
        isOpen={isOpen}
        toggle={onToggleModal}
        className={styles.Modal}
      >
        <ModalHeader
          toggle={onToggleModal}
          className={styles.ModalHeader}
          close={CloseButton}
        />

        <ModalBody
          className={styles.ModalBody}
        >
          <Label className="Label">
            Chỉnh sửa giờ Nhận - Trả phòng của Quý vị
          </Label>

          <hr className="Divider" />

          <div className="Section">
            <div className="Section__Title">
              Giờ nhận phòng
            </div>

            <div className="position-relative">
              <Flatpickr
                name="checkInTime"
                className="Section__Content__TimePicker"
                defaultValue={validation.values.checkInTime}
                options={optionsFlatpickr}
                onChange={handleTimeChange}
              />

              <i className="far fa-clock Section__Content__ClockIcon" />
            </div>
          </div>

          <hr className="Divider" />

          <div className="Section">
            <div className="Section__Title">
              Giờ trả phòng
            </div>

            <div className="position-relative">
              <Flatpickr
                name="checkOutTime"
                className="Section__Content__TimePicker"
                defaultValue={validation.values.checkOutTime}
                options={optionsFlatpickr}
                onChange={handleTimeChange}
              />

              <i className="far fa-clock Section__Content__ClockIcon" />
            </div>
          </div>

          <hr className="Divider" />

          <Row className="Section__Container mb-10">
            <Col md="4" className="Section__Title">
              Yêu cầu khách đặt cọc?
            </Col>

            <Col md="8" className="Section__Content__Wrapper">
              <div className="Section__Content__CheckBox">
                <label className="Section__Checkbox__Wrapper">
                  <Input
                    type="radio"
                    className="Section__Checkbox"
                    name="deposit"
                    value={1}
                    onChange={handleDepositChange}
                    checked={validation.values.allowDeposit}
                  />

                  <div className="Section__Checkbox__Name">
                    Có
                  </div>
                </label>

                <label className="Section__Checkbox__Wrapper">
                  <Input
                    type="radio"
                    className="Section__Checkbox"
                    name="deposit"
                    value={0}
                    onChange={handleDepositChange}
                    checked={!validation.values.allowDeposit}
                  />

                  <div className="Section__Checkbox__Name">
                    Không
                  </div>
                </label>
              </div>
            </Col>
          </Row>

          <Row className="Section mb-20">
            <Col md="12" className="Section__Title Section__TitleAmount">
              Loại yêu cầu đặt cọc
            </Col>

            <Col md="12" className="Section__ContentAmount">
              <AppSelectValidation
                name="depositType"
                isHiddenSearchable
                validation={validation}
                options={depositTypeOptions}
                value={validation.values.depositType}
                onChange={onChangeFilterOption}
                isDisabled={!validation.values.allowDeposit}
              />
            </Col>

          </Row>

          {validation.values.depositType === 'AMOUNT' ? (
            <Row className="Section">
              <Col md="12" className="Section__Title Section__TitleAmount">
                Số tiền yêu cầu đặt cọc
              </Col>

              <Col md="12" className="Section__ContentAmount">
                <div className="Amount">
                  <AppInputNumberValidation
                    name="amount"
                    _id="amount"
                    type="number"
                    placeholder="Nhập giá tiền yêu cầu đặt cọc"
                    validation={validation}
                    showCurrency="VNĐ"
                    isDisable={!validation.values.allowDeposit}
                    value={validation.values.amount}
                  />
                </div>

                <div className="Note">
                  Tối thiểu 100.000 VNĐ
                </div>
              </Col>
            </Row>
          ) : (
            <Row className="Section">
              <Col md="12" className="Section__Title Section__TitleAmount">
                Phần trăm yêu cầu đặt cọc
              </Col>

              <Col md="12" className="Section__ContentAmount">
                <div className="Amount">
                  <AppInputNumberValidation
                    name="depositPercent"
                    _id="depositPercent"
                    type="number"
                    placeholder="Nhập giá tiền yêu cầu đặt cọc"
                    validation={validation}
                    showCurrency="%"
                    isDisable={!validation.values.allowDeposit}
                    value={validation.values.depositPercent}
                  />
                </div>

                <div className="Note">
                  Tối thiểu 1% và &le;100%
                </div>
              </Col>
            </Row>
          )}

          <hr className="Divider" />

          <div className="Section__Policy">
            <div className="Section__Policy__Title">
              Tóm tắt chính sách
            </div>

            <div className="Section__Policy__Information__Wrapper">
              <div className="Section__Policy__Information__Point">•</div>

              <div className="Section__Policy__Information__Content">
                Giờ nhận phòng:
                {' '}
                {validation.values.checkInTime}
              </div>
            </div>

            <div className="Section__Policy__Information__Wrapper">
              <div className="Section__Policy__Information__Point">•</div>

              <div className="Section__Policy__Information__Content">
                Giờ trả phòng:
                {' '}
                {validation.values.checkOutTime}
              </div>
            </div>

            <div className="Section__Policy__Information__Wrapper">
              <div className="Section__Policy__Information__Point">•</div>

              <div className="Section__Policy__Information__Content">
                {validation.values.allowDeposit ? (
                  <>
                    Chính sách đặt cọc: Quý khách sẽ được yêu cầu đặt cọc
                    {' '}
                    {validation.values.depositType === 'AMOUNT' ? unparseCurrency(validation.values.amount) : validation.values.depositPercent || 0}
                    {validation.values.depositType === 'AMOUNT' ? ' VNĐ' : '%' }
                    {' '}
                    cho những dịch vụ phát sinh và hoàn trả khi làm thủ tục
                    trả phòng sau khi đã cần trừ hoàn tất
                  </>
                ) : (
                  'Quý khách sẽ được yêu cầu xuất trình chứng minh thư hoặc hộ chiếu khi làm thủ tục nhận phòng'
                )}
              </div>

            </div>
          </div>

          <hr className="Divider" />

          <div className="Button__Wrapper">
            <Button
              classes="Button Button__Cancel"
              onClick={onToggleModal}
            >
              Hủy
            </Button>

            <Button classes="Button Button__Save" onClick={validation.handleSubmit}>
              Lưu
            </Button>
          </div>
        </ModalBody>
      </Modal>
    </div>
  );
}

ModalEditPolicyReceiveRoom.propTypes = {
  checkInTime: PropTypes.string,
  checkOutTime: PropTypes.string,
  depositPolicy: PropTypes.instanceOf(Object),
  isOpen: PropTypes.bool.isRequired,

  actionGetPlaceDetail: PropTypes.func.isRequired,
  actionEditReceiveRoomPolicy: PropTypes.func.isRequired,
  onToggleModal: PropTypes.func.isRequired,
};

ModalEditPolicyReceiveRoom.defaultProps = {
  checkInTime: '',
  checkOutTime: '',
  depositPolicy: INITIAL_VALUE.OBJECT,
};

export default memo(ModalEditPolicyReceiveRoom);
